<template>
    <common-dropdown :isOpen="true" :hasArrow="false" arrowPositionY="bottom" arrowPositionX="right">
        <common-progress :percentage="profilePercentage" :isBarHidden="false" :isRounded="false"></common-progress>
        <common-content-spacer class="complete-profile-reminder__rating" height="micro">
            <p class="complete-profile-reminder__rating-percentage">
                {{ $t('elements.home.profile_reminder.percentage', { profilePercentage }) }}
            </p>
            <div style="positon: relative">
                <div class="complete-profile-reminder__circle"></div>
                <b class="complete-profile-reminder__rating-text-bold">
                    {{ $t('elements.home.profile_completion_reminder.go_to_profile') }}
                </b>
            </div>
            <div>
                <div
                    v-if="!$auth.isPracticeAncillary()"
                    class="status-container"
                    :class="npiContainerClass"
                    @click="handleNpiClick"
                >
                    <div class="status-check-box" :class="npiCheckBoxClass">
                        <common-icon name="white-check" size="small"></common-icon>
                    </div>
                    <div class="status-text">
                        <p>{{ $t('elements.home.profile_completion_reminder.verify_npi') }}</p>
                    </div>
                </div>

                <div class="status-container" :class="insuranceContainerClass" @click="handleInsuranceClick">
                    <div class="status-check-box" :class="insuranceCheckBoxClass">
                        <common-icon name="white-check" size="small"></common-icon>
                    </div>
                    <div class="status-text">
                        <p>{{ $t('elements.home.profile_completion_reminder.select_insurance') }}</p>
                    </div>
                </div>

                <div
                    v-if="$auth.isPracticeAncillary()"
                    class="status-container"
                    :class="serviceContainerClass"
                    @click="handleServiceClick"
                >
                    <div class="status-check-box" :class="serviceCheckBoxClass">
                        <common-icon name="white-check" size="small"></common-icon>
                    </div>
                    <div class="status-text">
                        <p>{{ $t('elements.home.profile_completion_reminder.select_services') }}</p>
                    </div>
                </div>
            </div>
        </common-content-spacer>
    </common-dropdown>
</template>

<script>
    export default {
        data() {
            return {
                isNpiVerified: false,
                isInsuranceSelected: false,
                isServiceSelected: false,
                hasAnyService: false,
                hasGroups: false,
                hasSubgroups: false,
            }
        },
        computed: {
            profilePercentage() {
                const basePercentage = 25
                const isAncillary = this.$auth.isPracticeAncillary()
                const fields = isAncillary
                    ? [this.isInsuranceSelected, this.isServiceSelected]
                    : [this.isNpiVerified, this.isInsuranceSelected]

                const completed = fields.filter(Boolean).length
                const total = fields.length

                const additionalPercentage = total > 0 ? (completed / total) * 75 : 0

                return Math.round(basePercentage + additionalPercentage)
            },
            npiContainerClass() {
                return { 'status-container--checked': this.isNpiVerified }
            },
            npiCheckBoxClass() {
                return { 'status-check-box--filled': this.isNpiVerified }
            },

            insuranceContainerClass() {
                return { 'status-container--checked': this.isInsuranceSelected }
            },
            insuranceCheckBoxClass() {
                return { 'status-check-box--filled': this.isInsuranceSelected }
            },

            serviceContainerClass() {
                return { 'status-container--checked': this.isServiceSelected }
            },
            serviceCheckBoxClass() {
                return { 'status-check-box--filled': this.isServiceSelected }
            },
        },
        methods: {
            handleNpiClick() {
                if (!this.isNpiVerified) {
                    this.$router.push({ name: 'profile', query: { tabType: 'professional' } })
                }
            },
            handleInsuranceClick() {
                if (!this.isInsuranceSelected) {
                    this.$router.push({ name: 'practice.insurance' })
                }
            },
            handleServiceClick() {
                const practice = this.$auth.getPractice()
                if (!this.hasAnyService) {
                    this.$router.push({ name: 'practice.services' })
                } else if (!this.hasGroups || !this.hasSubgroups) {
                    const incompleteService = practice.facilityServiceTypes.find((serviceType) => {
                        const hasGroups = serviceType.facilityServiceTypesGroups.length > 0
                        const hasSubgroups =
                            hasGroups &&
                            serviceType.facilityServiceTypesGroups.every(
                                (group) => group.facilityServiceTypesSubgroups.length > 0,
                            )
                        return !hasGroups || !hasSubgroups
                    })

                    if (incompleteService) {
                        this.$router.push({
                            name: 'practice.service.tests',
                            params: { serviceId: incompleteService.id },
                        })
                    } else {
                        this.$router.push({ name: 'practice.services' })
                    }
                }
            },
        },
        mounted() {
            const activeMember = this.$auth.getActiveMember()
            this.isNpiVerified = !!activeMember.isNpiVerified
            const practice = this.$auth.getPractice()

            if (practice && practice.insurancePlans.length > 0) {
                const onlySelfPay =
                    practice.insurancePlans.length === 1 && practice.insurancePlans[0].name.toLowerCase() === 'self-pay'
                this.isInsuranceSelected = !onlySelfPay
            } else {
                this.isInsuranceSelected = false
            }

            if (practice && practice.facilityServiceTypes.length > 0) {
                const allHaveGroups = practice.facilityServiceTypes.every(
                    (serviceType) => serviceType.facilityServiceTypesGroups.length > 0,
                )

                const allHaveSubgroups = allHaveGroups
                    ? practice.facilityServiceTypes.every((serviceType) =>
                          serviceType.facilityServiceTypesGroups.every((group) => group.facilityServiceTypesSubgroups.length > 0),
                      )
                    : false

                this.hasAnyService = true
                this.hasGroups = allHaveGroups
                this.hasSubgroups = allHaveSubgroups
                this.isServiceSelected = allHaveGroups && allHaveSubgroups
            } else {
                this.hasAnyService = false
                this.hasGroups = false
                this.hasSubgroups = false
                this.isServiceSelected = false
            }
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .status-container {
        display: flex;
        align-items: center;
        color: $color--txt-base-3;
        gap: rem(8px);
        font-size: $font-size--secondary;
        line-height: 19px;
        margin-bottom: rem(10px);
        font-size: $font-size--form;
        cursor: pointer;
        &--checked {
            color: $color--accent-2;
            cursor: default;
            pointer-events: none;
            .status-text {
                opacity: 0.6;
                transition: opacity 0.3s ease;
            }
        }

        .status-text {
            transition: opacity 0.3s ease;

            p {
                margin: 0;
            }
        }
        &:last-child {
            margin-bottom: 0;
        }
    }

    .status-check-box {
        display: flex;
        align-items: center;
        justify-content: center;
        height: rem(22px);
        width: rem(24px);
        border-radius: rem(2px);
        border: 2px solid $color--border-base;
        background-color: transparent;

        &--filled {
            border: 2px solid $color--accent-2;
            background-color: $color--accent-2;
        }
    }
</style>
